import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  clearCrossFilterDimension,
  clearCrossFilterStorage,
  cloneCrossFilter,
  countDimensionSelections,
  defaultWorkspaceColumnFilters,
  dimensionPendingChanges,
  emptyCrossFilter,
  filterWorkspaceEntities,
  hasMergeablePendingDimension,
  isCrossFilterEmpty,
  loadCrossFilter,
  mergeCrossFilterDimension,
  removeIdFromFilter,
  removeStalePins,
  saveCrossFilter,
  syncPendingDimension,
  type EntityCrossFilter,
  type EntityCrossFilterDimension,
  type EntityGraph,
  type WorkspaceColumnFilters,
} from '@/utils/entityCrossFilter';

export function useWorkspaceState(profileId: string) {
  const [appliedFilter, setAppliedFilter] = useState<EntityCrossFilter>(emptyCrossFilter);
  const [pendingFilter, setPendingFilter] = useState<EntityCrossFilter>(emptyCrossFilter);
  const [columnFilters, setColumnFilters] = useState<WorkspaceColumnFilters>(
    defaultWorkspaceColumnFilters
  );

  useEffect(() => {
    if (!profileId) return;
    const stored = loadCrossFilter(profileId);
    const initial = stored ?? emptyCrossFilter();
    setAppliedFilter(initial);
    setPendingFilter(cloneCrossFilter(initial));
    setColumnFilters(defaultWorkspaceColumnFilters());
  }, [profileId]);

  useEffect(() => {
    if (!profileId) return;
    const timer = window.setTimeout(() => {
      saveCrossFilter(profileId, appliedFilter);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [profileId, appliedFilter]);

  const applyDimensionFilter = useCallback(
    (dimension: EntityCrossFilterDimension) => {
      setAppliedFilter((prevApplied) => {
        const merged = mergeCrossFilterDimension(prevApplied, pendingFilter, dimension);
        setPendingFilter((prevPending) => ({
          ...prevPending,
          [dimension]: [...merged[dimension]],
        }));
        return merged;
      });
    },
    [pendingFilter]
  );

  const discardDimensionSelection = useCallback(
    (dimension: EntityCrossFilterDimension) => {
      setPendingFilter((prev) => syncPendingDimension(appliedFilter, prev, dimension));
    },
    [appliedFilter]
  );

  const clearAllFilters = useCallback(() => {
    const empty = emptyCrossFilter();
    setAppliedFilter(empty);
    setPendingFilter(empty);
    if (profileId) clearCrossFilterStorage(profileId);
  }, [profileId]);

  const clearDimension = useCallback((dimension: keyof EntityCrossFilter) => {
    setAppliedFilter((prev) => {
      const next = clearCrossFilterDimension(prev, dimension);
      setPendingFilter(cloneCrossFilter(next));
      return next;
    });
  }, []);

  const removeFromApplied = useCallback((dimension: keyof EntityCrossFilter, id: string) => {
    setAppliedFilter((prev) => {
      const next = removeIdFromFilter(prev, dimension, id);
      setPendingFilter(cloneCrossFilter(next));
      return next;
    });
  }, []);

  const removeStaleFromApplied = useCallback((graph: EntityGraph) => {
    setAppliedFilter((prev) => {
      const next = removeStalePins(graph, prev);
      setPendingFilter(cloneCrossFilter(next));
      return next;
    });
  }, []);

  const updateColumnFilters = useCallback((patch: Partial<WorkspaceColumnFilters>) => {
    setColumnFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const hasActiveCrossFilter = useMemo(
    () => !isCrossFilterEmpty(appliedFilter),
    [appliedFilter]
  );

  const dimensionSelectionMeta = useCallback(
    (dimension: EntityCrossFilterDimension) => ({
      selectionCount: countDimensionSelections(pendingFilter, dimension),
      hasPendingChanges: dimensionPendingChanges(appliedFilter, pendingFilter, dimension),
      canApply: hasMergeablePendingDimension(appliedFilter, pendingFilter, dimension),
    }),
    [appliedFilter, pendingFilter]
  );

  return {
    appliedFilter,
    pendingFilter,
    setPendingFilter,
    columnFilters,
    updateColumnFilters,
    applyDimensionFilter,
    discardDimensionSelection,
    dimensionSelectionMeta,
    clearAllFilters,
    clearDimension,
    removeFromApplied,
    removeStaleFromApplied,
    hasActiveCrossFilter,
    filterEntities: filterWorkspaceEntities,
  };
}
